// ADDED
import { useState, useCallback, useEffect } from "react";
import leaveIncentiveService from "../services/leaveIncentiveService";
import hrCalendarService from "../services/hrCalendarService";
import { SetAuditTrail } from "../../components/Reports/AuditTrailReportEmployeeSetup";

export const useLeaveIncentive = ({ user, apiFilterPayload, additionalApiFilterPayload, showGeneralMessage }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [employeeDetails, setEmployeeDetails] = useState([]);
  const [years, setYears] = useState([]);

  const fetchPreview = useCallback(async () => {
    // Only fetch if we have the month/year
    // if (!additionalApiFilterPayload?.month || !additionalApiFilterPayload?.year) return;

    const hasMonth = !!additionalApiFilterPayload?.month;
    const hasYear = !!additionalApiFilterPayload?.year;

    if ((hasMonth && !hasYear) || (!hasMonth && hasYear)) {
      showGeneralMessage("Please select both Month and Year", false);
      return;
    }
    setLoading(true);
    try {
      const res = await leaveIncentiveService.getPreview(apiFilterPayload, additionalApiFilterPayload);
      setData(res.data || []);
    } catch (err) {
      showGeneralMessage("Failed to load leave incentives", false);
      console.error("Preview Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, [apiFilterPayload, additionalApiFilterPayload]);

  const fetchEmployeePreview = useCallback(async (id) => {
    setLoading(true);
    try {
      const res = await leaveIncentiveService.getPreviewByEmployee(id);
      setEmployeeDetails(res.data || []);
    } catch (err) {
      showGeneralMessage("Failed to load employee details", false);
      console.error("Error fetching employee details:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearEmployeeDetails = () => {
    setEmployeeDetails([]);
  }

  const fetchYears = useCallback(async () => {
    try {
      const res = await hrCalendarService.getAll();
      setYears(res.data.map(y => ({ value: y.year, label: y.year })));
    } catch (err) {
      showGeneralMessage("Failed to load years", false);
      console.error(err);
    }
  }, [showGeneralMessage]);

  useEffect(() => {
    fetchPreview();
    fetchYears();
  }, [fetchPreview, fetchYears]);

  const processLeaveIncentive = async (param) => {
    try {

      await leaveIncentiveService.process(param);

      await SetAuditTrail({
        setupArea: "Leave Incentive",
        actionTaken: "process",
        field: "Leave Incentive",
        newValue: `-`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id,
      });

      return true;
    } catch (err) {
      throw err;
    }
  };

  const revertLeaveIncentive = async (param) => {
    try {

      await leaveIncentiveService.revert(param);

      await SetAuditTrail({
        setupArea: "Leave Incentive",
        actionTaken: "revert",
        field: "Leave Incentive",
        newValue: `-`,
        oldValue: "-",
        dateSaved: new Date(),
        savedById: user.id,
      });

      return true;
    } catch (err) {
      throw err;
    }
  };

  const releaseIncentive = async (id) => {
    setLoading(true);
    try {
      const res = await leaveIncentiveService.release(id);

      await SetAuditTrail({
        setupArea: "Leave Incentive",
        actionTaken: "release",
        field: "Release Payment",
        newValue: `Employee ID: ${id}`,
        oldValue: "Pending",
        dateSaved: new Date(),
        savedById: user.id,
      });

      await fetchPreview(); // Refresh the main table
      showGeneralMessage(res.data?.message || "Incentives released successfully", true);
      return true;
    } catch (err) {
      showGeneralMessage(err.response?.data?.message || "Release failed", false);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    data,
    employeeDetails,
    years,
    loading,
    refresh: fetchPreview,
    fetchEmployeePreview,
    clearEmployeeDetails,
    getLeaveIncentiveDetails: (id) => leaveIncentiveService.getById(id),
    processLeaveIncentive,
    revertLeaveIncentive,
    releaseIncentive,
  };
};